/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { useState, useMemo } from "react";
import { marked } from "marked";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { docSections, filterDocSections } from "./Documentation/DocSections";
import { Input } from "baseui/input";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";

export const Reference = (): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const [activeSection, setActiveSection] = useState<string>(
    docSections[0]?.id || ""
  );

  const SEARCH_INPUT_OVERRIDES = {
    Root: {
      style: {
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor: currentTheme.colors.primaryLight,
        borderRightColor: currentTheme.colors.primaryLight,
        borderBottomColor: currentTheme.colors.primaryLight,
        borderLeftColor: currentTheme.colors.primaryLight,
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        backgroundColor: currentTheme.colors.dropdownBackground,
        outline: "none",
        width: "220px",
        maxWidth: "220px",
        boxSizing: "border-box",
        marginLeft: "10px",
        marginBottom: "10px",
      },
    },
    InputContainer: {
      style: {
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.text,
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.medium,
        minWidth: 0,
      },
    },
    ClearIcon: {
      style: {
        color: currentTheme.colors.text,
        ":hover": {
          color: currentTheme.colors.primaryLight,
        },
      },
    },
    ClearIconContainer: {
      style: {
        backgroundColor: currentTheme.colors.inputNodeBackgroundColor,
        color: currentTheme.colors.text,
        flexShrink: 0,
      },
    },
  } as const;

  const [searchString, setSearchString] = useState<string>("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const currentSection = docSections.find((s) => s.id === activeSection);
  const filteredDocSections = filterDocSections(searchString);

  const SIDEBAR_WIDTH = isSidebarCollapsed ? 0 : 280;

  // Helper function to convert heading text to kebab-case ID
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .trim();
  };

  // Configure marked with custom renderer to add IDs to headings
  useMemo(() => {
    const renderer = new marked.Renderer();

    // Override heading rendering to add IDs
    renderer.heading = ({ text, depth }) => {
      const id = slugify(text);
      return `<h${depth} id="${id}">${text}</h${depth}>`;
    };

    marked.setOptions({
      gfm: true, // GitHub Flavored Markdown
      breaks: false,
      renderer: renderer, // Use the custom renderer
    });
  }, []);

  // Render markdown to HTML
  const renderedContent = useMemo(() => {
    if (!currentSection) return "";
    return marked(currentSection.content);
  }, [currentSection]);

  return (
    <div
      style={{
        display: "flex",
        position: "relative",
        height: "100%",
        fontFamily: currentTheme.fonts.primary,
        backgroundColor: currentTheme.colors.pageBackgroundColor,
        overflow: "hidden",
      }}
    >
      {/* Left Sidebar Navigation */}
      {!isSidebarCollapsed && (
        <aside
          style={{
            width: SIDEBAR_WIDTH,
            backgroundColor: currentTheme.colors.background,
            borderRight: `1px solid ${currentTheme.colors.controlBarBorderColor}`,
            overflowY: "auto",
            padding: "24px 0",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              padding: "0 24px 16px 24px",
              borderBottom: `1px solid ${currentTheme.colors.controlBarBorderColor}`,
              marginBottom: 16,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "1.5rem",
                color: currentTheme.colors.text,
                fontWeight: 600,
              }}
            >
              Documentation
            </h2>
          </div>

          <div className="nodrag" style={{ width: "90%", marginTop: 6 }}>
            <div data-testid="node-search-input-field">
              <Input
                clearable
                clearOnEscape={false}
                aria-label="Find"
                placeholder="Find..."
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                size="mini"
                overrides={SEARCH_INPUT_OVERRIDES}
              />
            </div>
          </div>
          <nav>
            {filteredDocSections.map((section) => (
              <React.Fragment key={section.id}>
                <button
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    width: "100%",
                    padding: "12px 24px",
                    border: "none",
                    backgroundColor:
                      activeSection === section.id
                        ? currentTheme.colors.backgroundTertiary
                        : "transparent",
                    color: currentTheme.colors.text,
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    fontWeight: activeSection === section.id ? 600 : 400,
                    borderLeft:
                      activeSection === section.id
                        ? `3px solid ${currentTheme.colors.primary}`
                        : "3px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (activeSection !== section.id) {
                      e.currentTarget.style.backgroundColor =
                        currentTheme.colors.backgroundTertiary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSection !== section.id) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {section.title}
                </button>

                {/* Subsections */}
                {section.subsections &&
                  activeSection === section.id &&
                  section.subsections.map((subsection) => (
                    <button
                      key={subsection.id}
                      onClick={() => {
                        const element = document.getElementById(subsection.id);
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 24px 8px 48px",
                        border: "none",
                        backgroundColor: "transparent",
                        color: currentTheme.colors.text,
                        textAlign: "left",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        opacity: 0.8,
                        transition: "opacity 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "0.8";
                      }}
                    >
                      {subsection.title}
                    </button>
                  ))}
              </React.Fragment>
            ))}
          </nav>
        </aside>
      )}

      {/* Toggle Sidebar Button */}
      <StatefulTooltip
        placement={PLACEMENT.right}
        content={isSidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
        overrides={getTooltipOverrides(currentTheme)}
      >
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          style={{
            position: "absolute",
            left: isSidebarCollapsed ? 0 : SIDEBAR_WIDTH - 20,
            top: 16,
            width: 40,
            height: 40,
            backgroundColor: currentTheme.colors.backgroundTertiary,
            border: `1px solid ${currentTheme.colors.controlBarBorderColor}`,
            borderRadius: "0 8px 8px 0",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: currentTheme.colors.text,
            fontSize: "1.2rem",
            zIndex: 10,
            transition: "left 0.3s ease",
          }}
        >
          {isSidebarCollapsed ? "→" : "←"}
        </button>
      </StatefulTooltip>

      {/* Main Content Area */}
      <main
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "48px 64px",
          backgroundColor: currentTheme.colors.pageBackgroundColor,
        }}
      >
        {currentSection && (
          <article>
            <h1
              style={{
                color: currentTheme.colors.text,
                fontSize: "2.5rem",
                fontWeight: 700,
                marginBottom: "24px",
                borderBottom: `2px solid ${currentTheme.colors.controlBarBorderColor}`,
                paddingBottom: "16px",
              }}
            >
              {currentSection.title}
            </h1>

            <div
              style={{
                color: currentTheme.colors.text,
                lineHeight: 1.7,
                fontSize: "1.0625rem",
              }}
              className="markdown-content"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
          </article>
        )}
      </main>

      <style>{`
        .markdown-content h2 {
          color: ${currentTheme.colors.text};
          font-size: 1.75rem;
          font-weight: 600;
          margin-top: 48px;
          margin-bottom: 16px;
          scroll-margin-top: 24px;
        }

        .markdown-content h3 {
          color: ${currentTheme.colors.text};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 32px;
          margin-bottom: 12px;
          scroll-margin-top: 24px;
        }

        .markdown-content h4 {
          color: ${currentTheme.colors.text};
          font-size: 1.2rem;
          font-weight: 600;
          margin-top: 24px;
          margin-bottom: 10px;
        }

        .markdown-content p {
          color: ${currentTheme.colors.text};
          margin-bottom: 16px;
        }

        .markdown-content code {
          background-color: ${currentTheme.colors.backgroundTertiary};
          color: ${currentTheme.colors.text};
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
          font-family: monospace;
        }

        .markdown-content pre {
          background-color: ${currentTheme.colors.backgroundTertiary};
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          overflow-x: auto;
        }

        .markdown-content pre code {
          background-color: transparent;
          padding: 0;
          border-radius: 0;
          display: block;
        }

        .markdown-content ul,
        .markdown-content ol {
          color: ${currentTheme.colors.text};
          margin-bottom: 16px;
          padding-left: 24px;
        }

        .markdown-content li {
          color: ${currentTheme.colors.text};
          margin-bottom: 8px;
        }

        .markdown-content blockquote {
          border-left: 4px solid ${currentTheme.colors.primary};
          padding-left: 16px;
          margin-left: 0;
          margin-bottom: 16px;
          font-style: italic;
          color: ${currentTheme.colors.text};
          opacity: 0.9;
        }

        .markdown-content a {
          color: ${currentTheme.colors.primary};
          text-decoration: underline;
        }

        .markdown-content a:hover {
          opacity: 0.8;
        }

        .markdown-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 16px;
          border: 1px solid ${currentTheme.colors.controlBarBorderColor};
        }

        .markdown-content th {
          background-color: ${currentTheme.colors.backgroundTertiary};
          color: ${currentTheme.colors.text};
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid ${currentTheme.colors.controlBarBorderColor};
          font-weight: 600;
        }

        .markdown-content td {
          color: ${currentTheme.colors.text};
          padding: 12px;
          border-bottom: 1px solid ${currentTheme.colors.controlBarBorderColor};
        }

        .markdown-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 16px 0;
        }

        .markdown-content hr {
          border: none;
          border-top: 1px solid ${currentTheme.colors.controlBarBorderColor};
          margin: 32px 0;
        }
      `}</style>
    </div>
  );
};
